<script setup>
import { ref, watch, nextTick } from "vue";
import { motion, AnimatePresence } from "motion-v";
import { RouterLink } from "vue-router";
import { onKeyStroke, usePreferredReducedMotion } from "@vueuse/core";
import { X, MessageCircle, ChevronRight } from "lucide-vue-next";

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  menuItems: { type: Array, required: true },
});

const emit = defineEmits(["update:modelValue", "navigate", "whatsapp"]);

const reducedMotion = usePreferredReducedMotion();
const panelRef = ref(null);
const closeBtnRef = ref(null);

const close = () => emit("update:modelValue", false);

onKeyStroke("Escape", () => {
  if (props.modelValue) close();
});

watch(
  () => props.modelValue,
  async (open) => {
    document.body.style.overflow = open ? "hidden" : "";
    if (open) {
      await nextTick();
      closeBtnRef.value?.focus();
    }
  }
);

const panelTransition = () =>
  reducedMotion.value === "reduce"
    ? { duration: 0 }
    : { duration: 0.32, ease: [0.16, 1, 0.3, 1] };

const scrimTransition = () =>
  reducedMotion.value === "reduce" ? { duration: 0 } : { duration: 0.2 };

const onNavigate = () => {
  emit("navigate");
  close();
};

const onWhatsApp = () => {
  emit("whatsapp");
  close();
};
</script>

<template>
  <Teleport to="body">
    <AnimatePresence>
      <motion.div
        v-if="modelValue"
        class="c4u-scrim"
        :initial="{ opacity: 0 }"
        :animate="{ opacity: 1 }"
        :exit="{ opacity: 0 }"
        :transition="scrimTransition()"
        @click="close"
      />
    </AnimatePresence>

    <AnimatePresence>
      <motion.div
        v-if="modelValue"
        ref="panelRef"
        class="c4u-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Menú de navegación"
        :initial="{ x: '100%' }"
        :animate="{ x: 0 }"
        :exit="{ x: '100%' }"
        :transition="panelTransition()"
      >
        <div class="c4u-panel__head">
          <span class="c4u-panel__title">Menú</span>
          <button
            ref="closeBtnRef"
            type="button"
            class="c4u-panel__close"
            aria-label="Cerrar menú"
            @click="close"
          >
            <X :size="24" :stroke-width="1.75" aria-hidden="true" />
          </button>
        </div>

        <nav class="c4u-panel__nav" aria-label="Navegación principal">
          <RouterLink
            v-for="item in menuItems"
            :key="item.to"
            :to="item.to"
            class="c4u-panel__link"
            @click="onNavigate"
          >
            <span>{{ item.name }}</span>
            <ChevronRight :size="18" :stroke-width="1.75" aria-hidden="true" />
          </RouterLink>
        </nav>

        <button type="button" class="c4u-panel__cta" @click="onWhatsApp">
          <MessageCircle :size="20" :stroke-width="1.75" aria-hidden="true" />
          Solicitar asesoría
        </button>
      </motion.div>
    </AnimatePresence>
  </Teleport>
</template>

<style scoped>
.c4u-scrim {
  position: fixed;
  inset: 0;
  background: rgba(10, 10, 10, 0.55);
  z-index: var(--c4u-z-overlay);
}

.c4u-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(85vw, 380px);
  background: var(--c4u-black);
  z-index: calc(var(--c4u-z-overlay) + 1);
  display: flex;
  flex-direction: column;
  padding: var(--c4u-space-md);
  padding-top: max(var(--c4u-space-md), env(safe-area-inset-top));
  padding-bottom: max(var(--c4u-space-lg), env(safe-area-inset-bottom));
  box-shadow: -12px 0 40px rgba(0, 0, 0, 0.35);
}

.c4u-panel__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-block: var(--c4u-space-xs);
  margin-bottom: var(--c4u-space-md);
  border-bottom: 1px solid var(--c4u-border-on-dark);
  padding-bottom: var(--c4u-space-md);
}

.c4u-panel__title {
  font-family: var(--c4u-font-display);
  font-weight: 600;
  font-size: var(--c4u-fs-body-lg);
  color: var(--c4u-text-on-dark);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.c4u-panel__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin: -10px;
  background: transparent;
  border: none;
  border-radius: var(--c4u-radius-md);
  color: var(--c4u-text-on-dark);
  cursor: pointer;
  transition: background-color var(--c4u-dur-fast) var(--c4u-ease-out);
}

.c4u-panel__close:hover {
  background: rgba(255, 255, 255, 0.08);
}

.c4u-panel__close:focus-visible {
  outline: 2px solid var(--c4u-focus-ring);
  outline-offset: 2px;
}

.c4u-panel__nav {
  display: flex;
  flex-direction: column;
  gap: var(--c4u-space-2xs);
  flex: 1;
}

.c4u-panel__link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--c4u-space-md) var(--c4u-space-xs);
  min-height: 44px;
  color: var(--c4u-text-on-dark);
  text-decoration: none;
  font-family: var(--c4u-font-display);
  font-weight: 500;
  font-size: var(--c4u-fs-body-lg);
  border-bottom: 1px solid var(--c4u-border-on-dark);
  transition: color var(--c4u-dur-fast) var(--c4u-ease-out);
}

.c4u-panel__link svg {
  color: var(--c4u-text-on-dark-muted);
  transition: transform var(--c4u-dur-fast) var(--c4u-ease-out);
  flex-shrink: 0;
}

.c4u-panel__link:hover,
.c4u-panel__link.router-link-active {
  color: var(--c4u-green-light);
}

.c4u-panel__link:hover svg {
  transform: translateX(3px);
}

.c4u-panel__link:focus-visible {
  outline: 2px solid var(--c4u-focus-ring);
  outline-offset: -2px;
}

.c4u-panel__cta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--c4u-space-xs);
  min-height: 52px;
  margin-top: var(--c4u-space-md);
  background: var(--c4u-green);
  color: var(--c4u-white);
  border: none;
  border-radius: var(--c4u-radius-md);
  font-family: var(--c4u-font-display);
  font-weight: 600;
  font-size: var(--c4u-fs-body);
  cursor: pointer;
  transition: background-color var(--c4u-dur-fast) var(--c4u-ease-out);
}

.c4u-panel__cta:hover {
  background: var(--c4u-green-dark);
}

.c4u-panel__cta:focus-visible {
  outline: 2px solid var(--c4u-focus-ring);
  outline-offset: 2px;
}

@media (min-width: 1024px) {
  .c4u-scrim,
  .c4u-panel {
    display: none;
  }
}
</style>
